import React, { useState } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Search, FileText, CheckCircle, Shield, Clock, BarChart, Database, Award, TrendingUp, AlertCircle, Car, IdCard } from 'lucide-react';
import { toast } from 'sonner';

const tiposDocumento = [
  { value: 'CC', label: 'Cédula de Ciudadanía' },
  { value: 'CE', label: 'Cédula de Extranjería' },
  { value: 'TI', label: 'Tarjeta de Identidad' },
  { value: 'PA', label: 'Pasaporte' },
  { value: 'NIT', label: 'NIT' },
];

export const ReportsPage = () => {
  const [searchType, setSearchType] = useState('placa');
  const [vinValue, setVinValue] = useState('');
  const [placa, setPlaca] = useState('');
  const [tipoDoc, setTipoDoc] = useState('');
  const [numeroDoc, setNumeroDoc] = useState('');
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const tipoDocLabel = tiposDocumento.find(t => t.value === tipoDoc)?.label || '';

  const handleSearch = () => {
    if (searchType === 'vin') {
      if (!vinValue.trim()) {
        toast.error('Por favor ingresa el VIN del vehículo');
        return;
      }
    } else {
      if (!placa.trim()) {
        toast.error('Por favor ingresa la placa del vehículo');
        return;
      }
      if (!tipoDoc) {
        toast.error('Por favor selecciona el tipo de documento');
        return;
      }
      if (!numeroDoc.trim()) {
        toast.error('Por favor ingresa el número de documento');
        return;
      }
    }
    setShowConfirmDialog(true);
  };

  const handleConfirmSearch = () => {
    setShowConfirmDialog(false);
    if (searchType === 'vin') {
      toast.success(`Buscando historial para VIN: ${vinValue}`);
      setTimeout(() => {
        window.location.href = `/informe?type=vin&value=${encodeURIComponent(vinValue)}`;
      }, 1500);
    } else {
      toast.success(`Buscando historial para placa: ${placa.toUpperCase()}`);
      setTimeout(() => {
        window.location.href = `/informe?placa=${encodeURIComponent(placa)}&tipoDoc=${tipoDoc}&numDoc=${encodeURIComponent(numeroDoc)}`;
      }, 1500);
    }
  };

  const features = [
    {
      icon: Database,
      title: 'Base de Datos Centralizada',
      description: 'Accede al historial completo almacenado en la nube de VERSO, la plataforma líder de la región.'
    },
    {
      icon: FileText,
      title: 'Historial Detallado',
      description: 'Visualiza todos los mantenimientos y reparaciones realizados en cualquier taller conectado a VERSO.'
    },
    {
      icon: Shield,
      title: 'Información Verificada',
      description: 'Datos verificados y certificados por talleres autorizados en toda la red VERSO.'
    },
    {
      icon: Clock,
      title: 'Historial Completo',
      description: 'Desde el primer mantenimiento hasta la última reparación, todo registrado y actualizado.'
    },
    {
      icon: BarChart,
      title: 'Valoración del Vehículo',
      description: 'Determina el valor real del vehículo basado en su historial de mantenimiento documentado.'
    },
    {
      icon: Award,
      title: 'Certificado de Autenticidad',
      description: 'Cada reporte incluye certificación de los datos consultados para mayor confianza.'
    }
  ];

  const benefits = [
    'Evita comprar vehículos con problemas ocultos',
    'Conoce el estado real de mantenimiento del vehículo',
    'Accede a información de talleres certificados en toda la red',
    'Toma decisiones informadas antes de comprar',
    'Verifica el kilometraje real del vehículo',
    'Historial actualizado en tiempo real desde la nube'
  ];

  const whatsIncluded = [
    'Historial completo de mantenimientos preventivos',
    'Registro de todas las reparaciones realizadas',
    'Detalles de repuestos e insumos utilizados',
    'Evidencia fotográfica de servicios realizados',
    'Listas de chequeo de calidad completadas',
    'Fecha y taller donde se realizó cada servicio',
    'Estado de planes de mantenimiento',
    'Diagnósticos registrados en el sistema'
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="relative py-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-purple-900/20 to-slate-900"></div>
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-purple-900/30 via-transparent to-transparent"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center space-x-2 bg-purple-500/10 border border-purple-500/30 rounded-full px-4 py-2 mb-6">
            <Database className="text-purple-400" size={16} />
            <span className="text-purple-300 text-sm font-medium">VERSO NUBE - Historial Vehicular</span>
          </div>
          
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            El historial completo de
            <br />
            <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
              tu vehículo en un solo lugar
            </span>
          </h1>
          <p className="text-gray-400 text-xl max-w-3xl mx-auto mb-12">
            Accede al repositorio central de historiales vehiculares de VERSO. Consulta todos los mantenimientos 
            y reparaciones realizados en talleres conectados a nuestra red en toda la región.
          </p>

          {/* Search Box */}
          <div className="max-w-2xl mx-auto">
            <Card className="bg-white/5 backdrop-blur-sm border-purple-500/20 p-8">
              {/* Toggle VIN / PLACA */}
              <div className="flex space-x-2 mb-6">
                <Button
                  onClick={() => setSearchType('vin')}
                  className={`flex-1 rounded-xl transition-all duration-300 ${
                    searchType === 'vin'
                      ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg'
                      : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                  }`}
                  data-testid="toggle-vin-btn"
                >
                  BUSCAR POR VIN
                </Button>
                <Button
                  onClick={() => setSearchType('placa')}
                  className={`flex-1 rounded-xl transition-all duration-300 ${
                    searchType === 'placa'
                      ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg'
                      : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                  }`}
                  data-testid="toggle-placa-btn"
                >
                  BUSCAR POR PLACA
                </Button>
              </div>

              <div className="space-y-5">
                {searchType === 'vin' ? (
                  /* Campo VIN */
                  <div>
                    <label className="text-gray-300 text-sm font-medium mb-2 block text-left">Número VIN</label>
                    <Input
                      type="text"
                      placeholder="Ej: JTDEPRAE5LJ051234"
                      value={vinValue}
                      onChange={(e) => setVinValue(e.target.value.toUpperCase())}
                      onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                      className="bg-white/10 border-purple-500/30 text-white placeholder:text-gray-500 h-14 text-lg rounded-xl focus:border-purple-500 focus:ring-purple-500 uppercase tracking-wider font-mono"
                      data-testid="search-vin-input"
                    />
                  </div>
                ) : (
                  /* Campos Placa + Documento */
                  <>
                    <div>
                      <label className="text-gray-300 text-sm font-medium mb-2 block text-left">Placa del vehículo</label>
                      <Input
                        type="text"
                        placeholder="Ej: ABC-123"
                        value={placa}
                        onChange={(e) => setPlaca(e.target.value.toUpperCase())}
                        className="bg-white/10 border-purple-500/30 text-white placeholder:text-gray-500 h-14 text-lg rounded-xl focus:border-purple-500 focus:ring-purple-500 uppercase tracking-widest font-mono"
                        data-testid="search-placa-input"
                      />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="text-gray-300 text-sm font-medium mb-2 block text-left">Tipo de documento</label>
                        <Select value={tipoDoc} onValueChange={setTipoDoc}>
                          <SelectTrigger
                            className="bg-white/10 border-purple-500/30 text-white h-14 text-base rounded-xl focus:border-purple-500 focus:ring-purple-500 [&>span]:text-gray-500 data-[state=open]:border-purple-500"
                            data-testid="search-tipo-doc-select"
                          >
                            <SelectValue placeholder="Seleccionar..." />
                          </SelectTrigger>
                          <SelectContent className="bg-slate-900 border-purple-500/30">
                            {tiposDocumento.map((tipo) => (
                              <SelectItem
                                key={tipo.value}
                                value={tipo.value}
                                className="text-white focus:bg-purple-500/20 focus:text-white cursor-pointer"
                              >
                                {tipo.value} - {tipo.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="text-gray-300 text-sm font-medium mb-2 block text-left">Número de documento</label>
                        <Input
                          type="text"
                          placeholder="Ej: 1234567890"
                          value={numeroDoc}
                          onChange={(e) => setNumeroDoc(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                          className="bg-white/10 border-purple-500/30 text-white placeholder:text-gray-500 h-14 text-base rounded-xl focus:border-purple-500 focus:ring-purple-500"
                          data-testid="search-numero-doc-input"
                        />
                      </div>
                    </div>
                  </>
                )}

                {/* Botón buscar */}
                <Button
                  onClick={handleSearch}
                  className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 h-14 text-lg rounded-xl shadow-lg shadow-purple-500/20"
                  data-testid="search-submit-btn"
                >
                  <Search className="mr-2" size={20} />
                  Consultar Historial Vehicular
                </Button>
              </div>
              
              <p className="text-gray-400 text-sm mt-4 text-center">
                Consulta el historial completo almacenado en la nube de VERSO
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section className="relative py-32">
        <div className="absolute inset-0 bg-gradient-to-b from-slate-900 via-black to-slate-900"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">¿Qué incluye el informe VERSO?</h2>
            <p className="text-gray-400 text-lg">Información detallada de cada servicio realizado al vehículo</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-4xl mx-auto mb-16">
            {whatsIncluded.map((item, index) => (
              <div key={index} className="flex items-start space-x-3 bg-white/5 backdrop-blur-sm border border-purple-500/20 p-4 rounded-xl">
                <CheckCircle className="text-purple-400 mt-1 flex-shrink-0" size={20} />
                <span className="text-gray-300">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="relative py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">Características del Sistema VERSO Nube</h2>
            <p className="text-gray-400 text-lg">La plataforma central de historiales vehiculares</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <Card key={index} className="bg-white/5 backdrop-blur-sm border-purple-500/20 p-6 hover:border-purple-500/40 transition-all duration-300 group">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500/20 to-pink-500/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                    <Icon className="text-purple-400" size={24} />
                  </div>
                  <h3 className="text-lg font-bold text-white mb-2">{feature.title}</h3>
                  <p className="text-gray-400 text-sm">{feature.description}</p>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="relative py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl font-bold text-white mb-6">
                ¿Por qué necesitas el
                <br />
                <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                  informe VERSO?
                </span>
              </h2>
              <p className="text-gray-400 text-lg mb-8">
                Toma decisiones inteligentes con información verificada y actualizada en tiempo real
              </p>

              <div className="space-y-4 mb-8">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <TrendingUp className="text-purple-400 mt-1 flex-shrink-0" size={20} />
                    <span className="text-gray-300">{benefit}</span>
                  </div>
                ))}
              </div>

              <Button 
                onClick={handleSearch}
                size="lg"
                className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white px-8 py-6 text-lg rounded-xl shadow-lg"
              >
                Obtener Informe Ahora
              </Button>
            </div>

            <Card className="bg-gradient-to-br from-purple-500/20 to-pink-500/20 border-purple-500/30 backdrop-blur-sm p-8">
              <div className="text-center mb-6">
                <Database className="text-purple-400 mx-auto mb-4" size={48} />
                <h3 className="text-2xl font-bold text-white mb-2">Historial Centralizado</h3>
                <p className="text-gray-300">Plataforma líder de historiales vehiculares</p>
              </div>
              <div className="space-y-4">
                <div className="bg-white/5 border border-purple-500/20 rounded-xl p-4">
                  <div className="text-3xl font-bold text-purple-400 mb-1">100%</div>
                  <div className="text-gray-400 text-sm">Datos Verificados</div>
                </div>
                <div className="bg-white/5 border border-purple-500/20 rounded-xl p-4">
                  <div className="text-3xl font-bold text-purple-400 mb-1">24/7</div>
                  <div className="text-gray-400 text-sm">Acceso Instantáneo</div>
                </div>
                <div className="bg-white/5 border border-purple-500/20 rounded-xl p-4">
                  <div className="text-3xl font-bold text-purple-400 mb-1">+15k</div>
                  <div className="text-gray-400 text-sm">Talleres Conectados</div>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-32">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="bg-gradient-to-br from-purple-500/20 to-pink-500/20 border-purple-500/30 backdrop-blur-sm p-12 text-center">
            <h2 className="text-4xl font-bold text-white mb-4">
              Consulta el Historial Antes de Comprar
            </h2>
            <p className="text-gray-300 text-lg mb-8">
              Únete a miles de usuarios que toman decisiones inteligentes con VERSO
            </p>
            <Button 
              onClick={handleSearch}
              size="lg"
              className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white px-12 py-6 text-lg rounded-xl"
            >
              <Search className="mr-2" size={20} />
              Buscar Historial Ahora
            </Button>
          </Card>
        </div>
      </section>

      {/* Modal de Confirmación de Datos */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent className="bg-slate-900 border border-purple-500/30 backdrop-blur-xl shadow-2xl shadow-purple-500/10 sm:rounded-2xl max-w-md" data-testid="confirm-search-dialog">
          <DialogHeader className="text-center sm:text-center">
            <div className="mx-auto w-16 h-16 rounded-full bg-gradient-to-br from-purple-500/20 to-pink-500/20 border border-purple-500/30 flex items-center justify-center mb-4">
              <Car className="text-purple-400" size={32} />
            </div>
            <DialogTitle className="text-2xl font-bold text-white">
              Confirma tus datos
            </DialogTitle>
            <DialogDescription className="text-gray-400 mt-2">
              Verifica que la información ingresada sea correcta antes de generar el informe
            </DialogDescription>
          </DialogHeader>

          <div className="my-4 space-y-4">
            <div className="bg-white/5 border border-purple-500/20 rounded-xl p-5 space-y-4">
              {searchType === 'vin' ? (
                /* Confirmación VIN */
                <>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-400 text-sm font-medium">Tipo de búsqueda</span>
                    <span className="bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xs px-3 py-1 rounded-full font-semibold">
                      VIN
                    </span>
                  </div>
                  <p className="text-white text-2xl font-mono font-bold tracking-widest" data-testid="confirm-vin-value">
                    {vinValue}
                  </p>
                </>
              ) : (
                /* Confirmación Placa + Documento */
                <>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-400 text-sm font-medium">Placa del vehículo</span>
                    <span className="bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xs px-3 py-1 rounded-full font-semibold">
                      PLACA
                    </span>
                  </div>
                  <p className="text-white text-2xl font-mono font-bold tracking-widest" data-testid="confirm-placa-value">
                    {placa}
                  </p>

                  <div className="border-t border-purple-500/10 pt-4">
                    <div className="flex items-center space-x-2 mb-1">
                      <IdCard className="text-purple-400" size={16} />
                      <span className="text-gray-400 text-sm font-medium">Documento de identidad</span>
                    </div>
                    <div className="flex items-center mt-2">
                      <span className="text-purple-300 text-xs font-semibold bg-purple-500/20 px-2 py-0.5 rounded-full">{tipoDoc}</span>
                      <span className="text-gray-500 text-xs ml-2">{tipoDocLabel}</span>
                    </div>
                    <p className="text-white text-lg font-mono font-bold tracking-wider mt-2" data-testid="confirm-doc-value">
                      {numeroDoc}
                    </p>
                  </div>
                </>
              )}
            </div>

            <div className="flex items-start space-x-3 bg-yellow-500/10 border border-yellow-500/20 rounded-xl p-4">
              <AlertCircle className="text-yellow-400 flex-shrink-0 mt-0.5" size={18} />
              <p className="text-yellow-300/80 text-sm">
                {searchType === 'vin'
                  ? 'Asegúrate de que el VIN esté escrito correctamente. Un dato incorrecto puede generar resultados erróneos.'
                  : 'Asegúrate de que la placa y el número de documento estén escritos correctamente. Datos incorrectos pueden generar resultados erróneos.'
                }
              </p>
            </div>
          </div>

          <DialogFooter className="flex flex-col sm:flex-row gap-3 sm:gap-2">
            <Button
              variant="outline"
              onClick={() => setShowConfirmDialog(false)}
              className="border-purple-500/30 text-gray-300 hover:bg-purple-500/10 hover:text-white rounded-xl"
              data-testid="confirm-cancel-btn"
            >
              Corregir datos
            </Button>
            <Button
              onClick={handleConfirmSearch}
              className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white rounded-xl shadow-lg shadow-purple-500/20"
              data-testid="confirm-search-btn"
            >
              <Search className="mr-2" size={16} />
              Confirmar y Buscar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};